import { t } from 'ember-intl';
import { PhFilePdf } from 'ember-phosphor-icons';
import ReSelect from 'tio-ui/components/select';
import type { TOC } from '@ember/component/template-only';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

/** One untagged upload plus whether its filing PATCH is currently in flight. */
export interface OtherDocumentRow {
  asset: TasAssetModel;
  isFiling: boolean;
}

interface OtherDocumentsListSignature {
  Args: {
    /** Live, correctly-classified uploads no categorized row can place (no
     * row key, no declared type — legacy-surface uploads). Empty → render
     * nothing. */
    rows: OtherDocumentRow[];
    /** The admin-configured document entries a document may be filed under. */
    entries: Array<{ label: string; type: string }>;
    /** Files `asset` under the entry with `label`: tags it like a categorized
     * upload so it moves into that row and counts toward the requirement. */
    onFile: (asset: TasAssetModel, label: string) => unknown;
    disabled?: boolean;
  };
  Element: HTMLDivElement;
}

const toSelectItems = (entries: Array<{ label: string; type: string }>) =>
  entries.map((entry) => ({ label: entry.label, key: entry.label }));

/**
 * Shows legacy uploads that count toward submission but lack a row key or declared type. Filing
 * under a category asserts its type and moves it into that row.
 */
const OtherDocumentsList: TOC<OtherDocumentsListSignature> = <template>
  {{#if @rows.length}}
    <div class="tio-col-extra-tight" data-test-other-documents ...attributes>
      <h4 class="tio-text-sm-semibold uppercase tracking-wider tio-fg-muted">
        {{t "tas.attachments.other_documents"}}
      </h4>
      <p class="text-xs tio-fg-muted">
        {{t "tas.attachments.other_documents_hint"}}
      </p>
      <div class="tio-col-extra-tight">
        {{#each @rows as |row|}}
          {{! The category select sets row height; keep vertical padding modest. }}
          <div
            class="tio-row-between gap-3 rounded border tio-border-default tio-bg-surface px-3 py-2"
            data-test-other-document
          >
            <a
              href={{row.asset.signedUrl}}
              target="_blank"
              rel="noopener noreferrer"
              class="tio-inline-row gap-2 flex-1 min-w-0 tio-fg-secondary hover:tio-fg-info text-sm"
              title={{row.asset.filename}}
              data-test-other-document-link
            >
              <PhFilePdf @size="18" class="shrink-0" />
              <span class="truncate">{{row.asset.filename}}</span>
            </a>
            {{#if @entries.length}}
              <div class="w-48 shrink-0" data-test-other-document-file-under>
                {{! Wait for this PATCH before allowing another category; concurrent writes would start from the same tags. }}
                <ReSelect
                  @items={{toSelectItems @entries}}
                  @onChange={{fn @onFile row.asset}}
                  @clearable={{false}}
                  @disabled={{or @disabled row.isFiling}}
                  @placeholder={{t "tas.attachments.file_under"}}
                  {{! Include the filename so assistive technology can distinguish each row’s category control. }}
                  @ariaLabel={{t "tas.attachments.file_under_document" filename=row.asset.filename}}
                />
              </div>
            {{/if}}
          </div>
        {{/each}}
      </div>
    </div>
  {{/if}}
</template>;

export default OtherDocumentsList;
